import './ConfirmModal.scss';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import {
  cloneElement,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmModalProps {
  className?: string;
  openButton: ReactElement;
  message?: ReactNode;
  title?: ReactNode;
  onOk?: Function;
  onCancel?: Function;
  afterConfirm?: Function;
  extraContent?: ReactNode;
  okDisabled?: boolean;
  beforeOpen?: (event: MouseEvent<HTMLElement>) => boolean | void;
}

const ConfirmModal = (props: ConfirmModalProps) => {
  const { t } = useTranslation();
  const {
    className,
    openButton,
    message,
    title,
    onOk,
    onCancel,
    extraContent,
    okDisabled,
    beforeOpen,
    afterConfirm,
  } = props;
  const [open, setOpen] = useState(openButton ? false : true);
  const handleOk = useCallback(() => {
    onOk?.();
    setOpen(false);
  }, [onOk]);
  const handleCancel = useCallback(() => {
    onCancel?.();
    setOpen(false);
  }, [onCancel]);
  const handleOpen = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Permite validar condiciones del formulario antes de mostrar la confirmación.
      if (beforeOpen?.(e) === false) {
        return;
      }

      setOpen(true);

      return;
    },
    [beforeOpen],
  );
  const openButtonWithOnClick = useMemo(
    () => (openButton ? cloneElement(openButton, { onClick: handleOpen }) : <></>),
    [openButton, handleOpen],
  );

  return (
    <>
      {openButtonWithOnClick}
      <Modal
        className={`ConfirmModal ${className}`}
        title={
          <div className="ConfirmModalTitle">
            <InfoCircleOutlined className="ConfirmModalIcon" />
            {title}
          </div>
        }
        open={open}
        onCancel={handleCancel}
        onOk={() => {
          handleOk();
          afterConfirm?.();
        }}
        okText={t('Aceptar')}
        okButtonProps={{ disabled: okDisabled }}
        cancelText={t('Cancelar')}
        closable={false}
        maskClosable={false}>
        <div className="ConfirmModalContent">
          {message}
          {extraContent}
        </div>
      </Modal>
    </>
  );
};

export default ConfirmModal;
